/**
 * Match Schedule Modal Component
 * Uses MatchScheduleForm for scheduling matches
 */

"use client";

import { rescheduleMatch, scheduleMatch } from "@/actions/match";
import {
  MatchScheduleForm,
  type MatchScheduleStreamingValue,
} from "@/components/forms/MatchScheduleForm";
import { useAuth } from "@/contexts/AuthContext";
import { useTournamentLocations } from "@/hooks/useTournamentLocations";
import { logMatchEvent } from "@/lib/analytics";
import { Tournament } from "@/types";
import { Modal, ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import { addToast } from "@heroui/toast";
import { startOfDay } from "date-fns";
import { useTranslations } from "next-intl";
import { useState } from "react";

interface MatchScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  tournamentId: Tournament["id"];
  matchId?: string;
  team1Name?: string;
  team2Name?: string;
  submittingTeamId?: string;
  /** Whether this is a schedule or reschedule action */
  mode?: "schedule" | "reschedule";
  /** The challenge expiry date from the match (used for reschedule max date) */
  challengeExpiresAt?: Date;
  /** Existing streaming selection on the match (reschedule prefill). */
  initialStreaming?: { courtNumber: number } | null;
  /** Existing match date on the match (reschedule prefill). */
  initialDate?: Date | null;
  /** Existing location id on the match (reschedule prefill). */
  initialLocationId?: string | null;
}

export function MatchScheduleModal({
  isOpen,
  onClose,
  onSuccess,
  tournamentId,
  matchId,
  team1Name,
  team2Name,
  submittingTeamId,
  mode = "schedule",
  challengeExpiresAt,
  initialStreaming,
  initialDate,
  initialLocationId,
}: MatchScheduleModalProps) {
  const t = useTranslations();
  const { user } = useAuth();
  const { data: locations } = useTournamentLocations(tournamentId);
  const [error, setError] = useState<string | null>(null);

  const isReschedule = mode === "reschedule";

  const handleSchedule = async (
    date: Date,
    locationId?: string,
    streaming?: MatchScheduleStreamingValue,
  ) => {
    setError(null);

    if (!matchId || !submittingTeamId || !user?.id || !locationId) {
      setError(t("matches.failedToSchedule"));
      return;
    }

    try {
      const action = isReschedule ? rescheduleMatch : scheduleMatch;
      const result = await action({
        matchId,
        locationId,
        matchDate: date,
        submittingTeamId,
        submittingUserId: user.id,
        ...(streaming !== undefined ? { streaming } : {}),
      });

      if (result.success) {
        addToast({
          title: isReschedule
            ? t("notifications.matchRescheduled.title")
            : t("notifications.matchScheduled.title"),
          color: "success",
        });
        if (result.warning === "streamingFailed") {
          addToast({
            title: t("matchSchedule.streamingFailed"),
            color: "warning",
            severity: "warning",
          });
        } else if (result.warning === "streamingCancelled") {
          addToast({
            title: t("matchSchedule.streamingCancelled"),
            color: "warning",
            severity: "warning",
          });
        }
        logMatchEvent("edit", matchId);
        onSuccess?.();
        onClose();
      } else {
        setError(result.error || t("matches.failedToSchedule"));
      }
    } catch (err) {
      console.error("Failed to schedule match:", err);
      setError(t("matches.failedToSchedule"));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      placement="center"
      onClose={onClose}
      isDismissable={false}
      isKeyboardDismissDisabled={true}
      hideCloseButton
      backdrop="opaque"
      size="lg"
      className="medieval-modal"
      classNames={{
        wrapper: "z-[200] max-sm:items-start",
        backdrop: "bg-black/80",
        base: "bg-[var(--bg-card)] border border-[var(--border-gold)] shadow-2xl max-sm:m-0 max-sm:rounded-none max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-full max-sm:max-w-full max-sm:border-0",
        header: "border-b border-[var(--border-subtle)]",
        body: "py-4 px-4 max-sm:flex-1 max-sm:overflow-y-auto",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 text-primary font-cinzel">
          <h3 className="text-xl">
            📜{" "}
            {isReschedule
              ? t("matches.rescheduleMatchAndLocation")
              : t("matches.scheduleMatchAndLocation")}
          </h3>
          <p className="text-sm text-secondary">
            {team1Name} VS {team2Name}
          </p>
        </ModalHeader>
        <ModalBody>
          <MatchScheduleForm
            onSubmit={handleSchedule}
            onCancel={onClose}
            locations={locations}
            requireLocation={true}
            submitLabel={
              isReschedule ? t("matches.rescheduleMatch") : undefined
            }
            maxDate={
              challengeExpiresAt
                ? startOfDay(challengeExpiresAt)
                : undefined
            }
            initialStreaming={initialStreaming}
            initialDate={isReschedule ? initialDate : undefined}
            initialLocationId={isReschedule ? initialLocationId : undefined}
          />
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800">
              <p className="text-sm text-danger-600 dark:text-danger-400">
                {error}
              </p>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
